/**
 * Debug Logger - Immediate Send Version
 *
 * Automatically appends timestamp, browser, page, user info to EVERY log.
 * Sends logs IMMEDIATELY to backend when functions are called (no 5-second delay).
 *
 * API Endpoints:
 * POST: https://test.ecaretherapy.com/api/dev_debug_ecare/debug_logs
 * GET:  https://test.ecaretherapy.com/api/dev_debug_ecare/debug_logs?lines=1000
 * DELETE: https://test.ecaretherapy.com/api/dev_debug_ecare/debug_logs
 *
 * Log file: /home/bitnami/Ecare/logs/debug_logs.log
 *
 * Environment Variable: REACT_APP_DEBUG_FILE_LOGS=true
 */

import axios from "axios";
import ConstConfig from "../configs/constConfig";

const DEBUG_ENABLED = process.env.REACT_APP_DEBUG_FILE_LOGS === "true";
const BATCH_SIZE = 10; // Smaller batch size for immediate sending
const SEND_IMMEDIATELY = true; // Send logs immediately on function calls

let pendingLogs = [];
let batchTimer = null;
let isSending = false; // Prevent simultaneous sends

/**
 * Get auto-appended details for every log
 */
const getAutoDetails = () => {
  try {
    // Get user from localStorage (encrypted)
    let userId = null;
    let userEmail = null;
    let userRole = null;
    try {
      const userData = localStorage.getItem("E_C_U_D");
      if (userData) {
        const decrypted = JSON.parse(atob(userData)); // Simplified - adjust based on your encryption
        userId = decrypted?.id || decrypted?._id;
        userEmail = decrypted?.email;
        userRole = decrypted?.role;
      }
    } catch (err) {
      // Silent fail
    }

    return {
      timestamp: new Date().toISOString(),
      datetime: new Date().toLocaleString("en-US", {
        timeZone: "America/New_York",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      }),
      browser: {
        userAgent: navigator.userAgent,
        language: navigator.language,
        platform: navigator.platform,
        online: navigator.onLine,
      },
      page: {
        url: window.location.href,
        pathname: window.location.pathname,
        search: window.location.search,
        hash: window.location.hash,
      },
      user: {
        id: userId,
        email: userEmail,
        role: userRole,
      },
      screen: {
        width: window.screen.width,
        height: window.screen.height,
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight,
        },
      },
    };
  } catch (err) {
    return {
      timestamp: new Date().toISOString(),
      datetime: new Date().toLocaleString(),
      error: "Failed to get auto details",
    };
  }
};

/**
 * Main debug log function
 */
const debugLog = (event, data = null, level = "debug") => {
  if (!DEBUG_ENABLED) return;

  try {
    const logEntry = {
      ...getAutoDetails(), // Auto-append all important details
      level,
      event,
      data: sanitize(data),
    };

    // Console output if enabled
    if (process.env.REACT_APP_CONSOLE_LOG === "true") {
      const method =
        level === "error"
          ? console.error
          : level === "warn"
          ? console.warn
          : console.log;
      method(`[${level.toUpperCase()}] ${event}:`, data);
    }

    // Add to pending batch
    pendingLogs.push(logEntry);

    // Send immediately if enabled or batch is full
    if (SEND_IMMEDIATELY || pendingLogs.length >= BATCH_SIZE) {
      if (batchTimer) {
        clearTimeout(batchTimer);
        batchTimer = null;
      }
      sendBatch();
    }
  } catch (err) {
    console.error("debugLog error:", err);
  }
};

/**
 * Error logging (use in catch blocks)
 */
debugLog.error = (functionName, error, context = null) => {
  debugLog(
    functionName,
    {
      errorMessage: error?.message || String(error),
      errorStack: error?.stack,
      errorName: error?.name,
      errorCode: error?.code,
      context,
    },
    "error"
  );
};

/**
 * Info logging
 */
debugLog.info = (functionName, message, data = null) => {
  debugLog(functionName, { message, data }, "info");
};

/**
 * Warning logging
 */
debugLog.warn = (functionName, message, data = null) => {
  debugLog(functionName, { message, data }, "warn");
};

/**
 * Function entry
 */
debugLog.entry = (functionName, params = null) => {
  debugLog(`${functionName} → ENTRY`, { params }, "debug");
};

/**
 * Function exit
 */
debugLog.exit = (functionName, result = null) => {
  debugLog(`${functionName} → EXIT`, { result }, "debug");
};

/**
 * API logging
 */
debugLog.api = (endpoint, method, payload = null, response = null) => {
  debugLog(
    "API_CALL",
    {
      endpoint,
      method,
      payload,
      response,
      responseStatus: response?.status,
      responseMessage: response?.message,
    },
    "info"
  );
};

/**
 * State change logging
 */
debugLog.state = (stateName, oldValue, newValue) => {
  debugLog(
    "STATE_CHANGE",
    {
      stateName,
      from: oldValue,
      to: newValue,
    },
    "debug"
  );
};

/**
 * Performance logging
 */
debugLog.performance = (label, duration) => {
  debugLog(
    "PERFORMANCE",
    {
      label,
      duration: `${duration}ms`,
      durationMs: duration,
    },
    "info"
  );
};

/**
 * Get auth token from localStorage
 */
const getAuthToken = () => {
  try {
    const token = localStorage.getItem("E_C_A_T");
    return token || null;
  } catch (err) {
    return null;
  }
};

/**
 * Send batch to backend (direct axios call, no React hooks)
 */
const sendBatch = async () => {
  if (pendingLogs.length === 0 || isSending) return;

  isSending = true;
  const logsToSend = [...pendingLogs];
  pendingLogs = [];

  try {
    const token = getAuthToken();
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    await axios.post(
      `${ConstConfig.MAIN_URL}/dev_debug_ecare/debug_logs`,
      { logs: logsToSend },
      { headers }
    );
  } catch (err) {
    // Re-add if failed
    pendingLogs = [...logsToSend, ...pendingLogs];
    console.error("Failed to send debug logs:", err.message || err);
  } finally {
    isSending = false;
  }
};

/**
 * Manual sync (force send now)
 */
debugLog.sync = async () => {
  if (batchTimer) {
    clearTimeout(batchTimer);
    batchTimer = null;
  }
  await sendBatch();
};

/**
 * Download logs from backend
 */
debugLog.download = async () => {
  try {
    await debugLog.sync(); // Sync pending first

    const token = getAuthToken();
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    const res = await axios.get(
      `${ConstConfig.MAIN_URL}/dev_debug_ecare/debug_logs?lines=1000`,
      { headers }
    );

    if (res?.data?.status === 1 && res?.data?.data?.logs) {
      const blob = new Blob([JSON.stringify(res.data.data.logs, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `debug-logs-${
        new Date().toISOString().split("T")[0]
      }.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } else {
      console.log("No logs found");
    }
  } catch (err) {
    console.error("Download failed:", err);
  }
};

/**
 * Clear all logs
 */
debugLog.clear = async () => {
  try {
    pendingLogs = [];

    const token = getAuthToken();
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    await axios.delete(`${ConstConfig.MAIN_URL}/dev_debug_ecare/debug_logs`, {
      headers,
    });
  } catch (err) {
    console.error("Clear failed:", err);
  }
};

/**
 * Get pending count
 */
debugLog.getPending = () => pendingLogs.length;

/**
 * Get pending logs (for debugging)
 */
debugLog.viewPending = () => {
  console.table(pendingLogs);
  return pendingLogs;
};

/**
 * Sanitize data
 */
const sanitize = (data) => {
  try {
    return JSON.parse(
      JSON.stringify(data, (key, value) => {
        // Remove sensitive data
        if (key === "password" || key === "token" || key === "secret") {
          return "[REDACTED]";
        }
        // Handle media tracks
        if (
          key === "localVideoTrack" ||
          key === "remoteVideoTrack" ||
          key === "localAudioTrack" ||
          key === "remoteAudioTrack"
        ) {
          return value ? "[MediaTrack]" : null;
        }
        if (typeof value === "function") return "[Function]";
        if (value instanceof Element) return "[DOMElement]";
        if (value === undefined) return "[undefined]";
        if (value === null) return null;
        return value;
      })
    );
  } catch (err) {
    return { sanitizeError: err.message, type: typeof data };
  }
};

// Send pending logs before page unload
if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", () => {
    if (pendingLogs.length > 0) {
      navigator.sendBeacon(
        `${ConstConfig.MAIN_URL}/dev_debug_ecare/debug_logs`,
        JSON.stringify({ logs: pendingLogs })
      );
    }
  });
}

export default debugLog;
